import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["canRouteToAvailable", "currentAssignee", "currentUserId", "loadingAssignee", "objectTypeId", "reassign", "teams", "workspaceId", "usageTracker", "isUngatedForWorkingHours", "isUngatedForHelpDeskRoutingScopeFiltering"];
/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */

import I18n from 'I18n';
import UISelect from 'UIComponents/input/UISelect';
import { useCallback, useMemo, useState } from 'react';
import debounce from 'react-utils/debounce';
import UsersAndTeamsRoutingRules from '../../routing-rules/records/UsersAndTeamsRoutingRules';
import { ASSIGNED, SELF_ASSIGNED, TEAM_ASSIGNED, UNASSIGNED } from '../constants/AssignedStates';
import { useAssignmentOptionRendererHD } from '../hooks/useAssignmentOptionRenderer';
import { useLoadOptionsHD } from '../hooks/useLoadOptionsHD';
import { getAgentId, getBotId } from '../operators/assignees';
import { currentAssigneeValueRenderer } from '../operators/currentAssigneeValueRenderer';
import { checkIsTeamAssignee } from '../utils/assignee-options';
import { getOpenedOwnerDropdown, getSearchedOwnerDropdown } from '../utils/event-handlers';
import AsyncUIAvatar from './AsyncUIAvatar';
import NoResultsFooter from './NoResultsFooter';
import { jsx as _jsx } from "react/jsx-runtime";
const useAssignmentValueRenderer = (currentAssignee, loadingAssignee) => useMemo(() => currentAssigneeValueRenderer({
  AsyncUIAvatar,
  currentAssignee,
  loadingAssignee
}), [currentAssignee, loadingAssignee]);
const getUserAssignment = (id, currentUserId) => {
  let assignment = ASSIGNED;
  if (id === currentUserId) {
    assignment = SELF_ASSIGNED;
  }
  return assignment;
};
const gatherTeamIds = teams => {
  let ids = [];
  for (const team of teams) {
    ids.push(team.id);
    if (team.childTeams.length > 0) {
      ids = ids.concat(gatherTeamIds(team.childTeams));
    }
  }
  return ids;
};
const ReassignmentDropdown = _ref => {
  let {
      canRouteToAvailable,
      currentAssignee,
      currentUserId,
      loadingAssignee,
      objectTypeId,
      reassign,
      teams,
      workspaceId,
      usageTracker,
      isUngatedForWorkingHours,
      isUngatedForHelpDeskRoutingScopeFiltering
    } = _ref,
    selectProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const currentAssigneeId =
  // @ts-expect-error Transmute's types think that its get function can't work with null/undefined but
  // if currentAssignee is either null or undefined, get will short circuit and return undefined
  getAgentId(currentAssignee) || getBotId(currentAssignee);
  const teamIds = useMemo(() => new Set(gatherTeamIds(teams)), [teams]);
  const loadOptions = useLoadOptionsHD({
    currentUserId,
    objectTypeId,
    teams,
    workspaceId,
    isUngatedForHelpDeskRoutingScopeFiltering
  });
  const [open, setOpen] = useState(false);
  const trackUsage = useCallback(usageProperties => usageTracker && usageTracker.track(usageProperties.key, usageProperties.properties), [usageTracker]);
  const handleUnassign = useCallback(() => {
    setOpen(false);
    reassign(UNASSIGNED);
  }, [reassign]);
  const optionRenderer = useAssignmentOptionRendererHD({
    canRouteToAvailable,
    currentAssigneeId,
    currentUserId,
    handleUnassign,
    workspaceId,
    isUngatedForWorkingHours,
    isUngatedForHelpDeskRoutingScopeFiltering
  });
  const valueRenderer = useAssignmentValueRenderer(currentAssignee, loadingAssignee);
  const handleChange = useCallback(event => {
    const {
      value: option
    } = event.target;
    const id = option.value;
    const isTeamOption = checkIsTeamAssignee(option);
    if (isTeamOption && teamIds.has(id)) {
      reassign(TEAM_ASSIGNED, {
        assignmentMethod: 'SYSTEM_AUTOMATIC',
        routingRuleDefinition: new UsersAndTeamsRoutingRules({
          teamIds: [id]
        }),
        shouldReassign: true
      });
    } else if (!isTeamOption && id !== currentAssigneeId) {
      // Only ASSIGNED | SELF_ASSIGNED
      const userAssignment = getUserAssignment(id, currentUserId);
      reassign(userAssignment, option.responder);
    }
  }, [currentAssigneeId, currentUserId, reassign, teamIds]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedTrackUsage = useCallback(debounce(() => trackUsage(getSearchedOwnerDropdown()), 1000), [trackUsage, getSearchedOwnerDropdown]);
  const handleOnInputChange = useCallback(() => {
    debouncedTrackUsage();
  }, [debouncedTrackUsage]);
  const handleOnOpenChange = useCallback(event => {
    const isOpen = event.target.value;
    setOpen(isOpen);
    if (isOpen) {
      trackUsage(getOpenedOwnerDropdown());
    }
  }, [setOpen, trackUsage]);
  return /*#__PURE__*/_jsx(UISelect, Object.assign({
    buttonUse: "link",
    cache: false,
    filterOptions: false,
    loadOptions: loadOptions,
    menuStyle: {
      maxHeight: 320
    },
    menuWidth: 360,
    minimumSearchCount: 0,
    noResultsText: isUngatedForHelpDeskRoutingScopeFiltering ? NoResultsFooter : null,
    onInputChange: handleOnInputChange,
    onOpenChange: handleOnOpenChange,
    onSelectedOptionChange: handleChange,
    open: open,
    optionRenderer: optionRenderer,
    placement: "bottom right",
    resultsClassName: "flat-result-options",
    searchable: true,
    searchPlaceholder: I18n.text('routing-ui.reassignment.searchPlaceholderText'),
    value: currentAssigneeId || '',
    valueRenderer: valueRenderer
  }, selectProps));
};
ReassignmentDropdown.displayName = 'ReassignmentDropdown';
export default ReassignmentDropdown;